import styled, { keyframes } from "styled-components";
import { useTranslation } from "react-i18next";
import { Loader2, Plus } from "lucide-react";
import type { ChatSession } from "@KassaroAB/shared/types/chatSession";
import colors from "@styling/colors";
import { borderRadius, spacing } from "@styling/sizing";
import { fontSizes } from "@styling/fonts";
import { formatDate } from "../utils/dateUtils";

const spin = keyframes`
  to {
    transform: rotate(360deg);
  }
`;

const Root = styled.aside`
  width: 300px;
  flex-shrink: 0;
  border-right: 1px solid ${colors.border};
  display: flex;
  flex-direction: column;
  min-height: 0;
  background-color: ${colors.backgroundDark};
  @media (max-width: 768px) {
    width: 100%;
    max-height: 40vh;
    border-right: none;
    border-bottom: 1px solid ${colors.border};
  }
`;

const NewChatButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${spacing.x1}px;
  width: 100%;
  padding: ${spacing.x2}px ${spacing.x3}px;
  border: none;
  border-bottom: 1px solid ${colors.border};
  background: none;
  cursor: pointer;
  font-size: ${fontSizes.small}px;
  font-weight: 600;
  color: ${colors.primary};
  text-align: left;
  &:hover {
    background-color: ${colors.background};
  }
`;

const SpinWrap = styled.span`
  display: inline-flex;
  animation: ${spin} 0.8s linear infinite;
`;

const ListScroll = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: ${spacing.x1}px;
`;

const SessionRow = styled.button<{ $active: boolean }>`
  width: 100%;
  text-align: left;
  padding: ${spacing.x2}px ${spacing.x3}px;
  border: none;
  border-radius: ${borderRadius.regular}px;
  cursor: pointer;
  background-color: ${({ $active }) => ($active ? colors.background : "transparent")};
  color: ${colors.text};
  display: flex;
  flex-direction: column;
  gap: ${spacing.x05}px;
  margin-bottom: ${spacing.x1}px;
  &:hover {
    background-color: ${colors.background};
  }
`;

const SessionRowTitle = styled.div`
  font-size: ${fontSizes.small}px;
  font-weight: 600;
  line-height: 1.35;
  color: ${colors.text};
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const SessionRowMeta = styled.div`
  font-size: ${fontSizes.small}px;
  color: ${colors.textMuted};
`;

const StatusLine = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.x2}px;
  font-size: ${fontSizes.small}px;
  color: ${colors.textMuted};
  padding: ${spacing.x2}px ${spacing.x3}px;
`;

const EmptyState = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${spacing.x4}px;
  font-size: ${fontSizes.regular}px;
  color: ${colors.textMuted};
  text-align: center;
`;

type Props = {
  sessions: ChatSession[];
  selectedId: string | null;
  isNewChat: boolean;
  isPendingNewChat: boolean;
  isLoading: boolean;
  onSelect: (id: string) => void;
  onNewChat: () => void;
};

export function ChatSessionSidebar({
  sessions,
  selectedId,
  isNewChat,
  isPendingNewChat,
  isLoading,
  onSelect,
  onNewChat,
}: Props) {
  const { t } = useTranslation();

  return (
    <Root>
      <NewChatButton type="button" onClick={onNewChat}>
        <Plus size={14} />
        {t("storeChatSessions.newChat")}
      </NewChatButton>
      {isLoading && (
        <StatusLine>
          <SpinWrap>
            <Loader2 size={16} aria-hidden />
          </SpinWrap>
          {t("common.loading")}
        </StatusLine>
      )}
      {!isLoading && sessions.length === 0 && !isNewChat && !isPendingNewChat && (
        <EmptyState>{t("storeChatSessions.emptyList")}</EmptyState>
      )}
      <ListScroll>
        {(isNewChat || isPendingNewChat) && (
          <SessionRow type="button" $active={isNewChat} onClick={onNewChat}>
            <SessionRowTitle>{t("storeChatSessions.newChat")}</SessionRowTitle>
          </SessionRow>
        )}
        {sessions.map((session) => (
          <SessionRow
            key={session.id}
            type="button"
            $active={!isNewChat && selectedId === session.id}
            onClick={() => onSelect(session.id)}
          >
            <SessionRowTitle>{session.name ?? "Genererar namn..."}</SessionRowTitle>
            <SessionRowMeta>
              {formatDate(session.updatedAt ?? session.createdAt)}
            </SessionRowMeta>
          </SessionRow>
        ))}
      </ListScroll>
    </Root>
  );
}
